import { toDayjsDateFormat } from '@flowus/common/i18n';
import { useAppTranslation } from '@flowus/common/i18n/use-translation';
import { getCurrencySymbols } from '@flowus/common';
import { cx } from '@flowus/common/cx';
import { useClipboard } from '@flowus/common/hooks/use-clipboard';
import { SearchParams } from '@flowus/login/const';
import { BlockType, PermissionRole, PermissionType } from '@next-space/fe-api-idl';
import dayjs from 'dayjs';
import type { FC } from 'react';
import { useEffect, useRef, useState } from 'react';
import { Icon } from 'src/common/components/icon';
import { ListItemType, ListView } from 'src/common/components/list-view';
import { message } from 'src/common/components/message';
import { useOpenModal } from 'src/common/components/next-modal';
import { Switch } from 'src/common/components/switch';
import { Tooltip } from 'src/common/components/tooltip';
import { createMenuDataContext } from 'src/editor/component/menu-list/menu-item';
import { DateTimePicker } from 'src/editor/editor/plugin/date-picker';
import { segmentsToText } from 'src/editor/utils/editor';
import { ActivityIds } from 'src/hooks/activities/activity-ids';
import { ActivitiesListType } from 'src/hooks/activities/use-activity';
import { useUpdateTask } from 'src/hooks/activities/use-update-task';
import { useReadonly } from 'src/hooks/page';
import { usePublicShare } from 'src/hooks/page/use-public-share';
import { getPermissions, usePermissions } from 'src/hooks/share/use-permissions';
import { useCurrentSpace } from 'src/hooks/space/use-current-space';
import { isTeamProSpace, useIsFreeSpaceLimit } from 'src/hooks/space/use-is-pro-space';
import { transaction } from 'src/hooks/use-transaction';
import { TRANSACTION_FIRE } from 'src/redux/actions';
import {
  removeBlockPermission,
  setBlockPermission,
} from 'src/redux/managers/block/update-permission';
import { dispatch } from 'src/redux/store';
import { bizTracker } from 'src/utils/biz-tracker';
import { isPageLike } from 'src/utils/block-type-utils';
import { writeTextInClipboard } from 'src/utils/clipboard';
import { judgeSharePage } from 'src/utils/getPageId';
import { usePickBlock } from 'src/utils/pick-block';
import { elementToGetBoundingClientRect } from 'src/utils/virtualElement';
import { ProIcon } from 'src/views/main/header/pro-icon';
import { OpenSettingFrom, SettingMenuType } from 'src/views/main/setting-modal/type';
import { useOpenAskUpgradeSpace } from 'src/views/main/setting-modal/use-open-ask-upgrade-space';
import { useOpenSettingModal } from 'src/views/main/setting-modal/use-open-setting-modal';
import { setPermission, useGetSharePageUrl } from '../helper';
import { ShareButtons } from '../share-buttons';
import { computePeriod } from '../share-pay';
import type { ShareProps } from '../types';
import { Panel } from '../types';
import { ShareMainFooter } from './share-main-footer';
import { ShareMainInvite } from './share-main-invite';
import { isBuildIn } from '@flowus/common/build-in';
import { FeatureFlags } from 'src/feature-flags';

type Props = Pick<ShareProps, 'uuid' | 'onChangeTab'>;

enum ShowType {
  UserListTab,
  ShareTab,
}

export const ShareMain: FC<Props> = (props) => {
  const { t } = useAppTranslation();
  const [showTab, setShowTab] = useState(ShowType.ShareTab);

  const ShareMainList = [
    { title: t('common.const.public_share'), type: ShowType.ShareTab },
    { title: t('common.invite_collaboration'), type: ShowType.UserListTab },
  ];

  return (
    <>
      <div className="text-t2 flex items-center px-4 space-x-2 py-2 border-b border-grey6">
        {ShareMainList.map((item) => (
          <div
            key={item.title}
            onClick={() => setShowTab(item.type)}
            className={cx(
              'relative animate-hover px-1.5 py-1 mb-px',
              item.type === showTab && 'bg-black_006'
            )}
          >
            {item.title}
          </div>
        ))}
      </div>

      {showTab === ShowType.UserListTab && <UserListTab {...props} />}
      {showTab === ShowType.ShareTab && <ShareTab {...props} />}
    </>
  );
};

const UserListTab: FC<Props> = (props) => {
  const { onChangeTab, uuid } = props;

  return <ShareMainInvite onChangeTab={onChangeTab} uuid={uuid} />;
};

const ShareTab: FC<Props> = (props) => {
  const { uuid, onChangeTab } = props;
  const { t } = useAppTranslation();
  const dateFormat = toDayjsDateFormat(t('common.date_format_ymd_no_time'));
  const openModal = useOpenModal();
  const updateTask = useUpdateTask();
  const { set, permissionsState, roleStatus } = usePublicShare(uuid);
  const {
    permissions,
    shared,
    illegal,
    role,
    publicRole,
    allowDuplicate,
    allowShowBreadcrumb,
    isRestricted,
    parentId,
    allowShowSidebar,
    allowShowAIChat,
    allowSeo,
    password,
    allowSubscribe,
    allowDownload,
    linkValidDate,
    accessFee,
    allowSelectionCopy,
  } = permissionsState;
  const currentSpace = useCurrentSpace();

  let { abandonedPublicShare } = currentSpace.setting ?? {};

  if (!isTeamProSpace(currentSpace.planType)) {
    abandonedPublicShare = false;
  }
  const block = usePickBlock(uuid, ['data'], ['segments']);
  // 如果已经禁止分享，已经分享过的页面允许关闭，但一旦关闭就无法再打开
  const disableSwitchPublicShare = abandonedPublicShare && !shared;

  /** issue: https://flowus.cn/eadc6101-5a96-4ed5-83fc-cb89a2c80675 */
  // const parentPermission = usePermissions(block?.parentId);
  // const isRoot = block?.parentId === block?.spaceId;
  const readonly = useReadonly(uuid);
  const isPDFFile =
    block?.type === BlockType.FILE &&
    segmentsToText(block.data.segments).toLowerCase().endsWith('.pdf');

  const isFreeSpaceLimit = useIsFreeSpaceLimit();
  // 打开了分享或者修改了可查看/可评论选项
  const openShareOrSwitchViewType = useRef(false);

  const { url, text, textAndUrl } = useGetSharePageUrl(uuid)();
  const openSettingModal = useOpenSettingModal();
  const openAskUpgradeSpace = useOpenAskUpgradeSpace();

  // usePublicShare(uuid);

  const copyShareId = useClipboard({ noMessage: true });

  const copyLink = password
    ? [`${url}${text}\n`, `${t('common.access_password')}${password}`].join('')
    : textAndUrl;

  const onCopyLink = async () => {
    await writeTextInClipboard(copyLink, {
      message: password
        ? t('components.share.link_and_password_copied')
        : t('common.components.link_widget.link_copied'),
    });
    bizTracker.event('share_copylink', { is_share: shared });
  };

  const copyEmbedLink = async () => {
    const urlObj = new URL(url);
    urlObj.searchParams.set(SearchParams.embed, 'true');
    await writeTextInClipboard(urlObj.href, {
      message: t('common.components.link_widget.link_copied'),
    });
    bizTracker.event('click_share_btn', {
      type: 'embedded',
    });
  };

  useEffect(() => {
    return () => {
      if (openShareOrSwitchViewType.current) {
        const { publicRole } = getPermissions(uuid);
        bizTracker.event('share_authority', {
          authority_type: publicRole === PermissionRole.COMMENTER ? 'can_comment' : 'can_read',
        });
      }
    };
  }, [uuid]);

  if (!block) return null;

  const isEditor = role === PermissionRole.EDITOR;

  const onClickSwitch = async () => {
    if (readonly || !isEditor || disableSwitchPublicShare) return;
    if (isFreeSpaceLimit && !shared && !isBuildIn()) {
      openAskUpgradeSpace(() => {
        openSettingModal({
          defaultMenuType: SettingMenuType.upgrade,
          from: OpenSettingFrom.link_valid,
        });
      });
      return;
    }
    if (accessFee && shared) {
      const goOn = await new Promise((resolve) => {
        openModal.warning({
          title: t('components.share.paid_share_warning'),
          confirm: () => {
            resolve(true);
          },
          cancel: () => {
            resolve(false);
          },
          closeBeforeCallBackForClickOutSide: () => {
            resolve(false);
          },
        });
      });
      if (!goOn) return;
    }

    if (!isRestricted && shared && parentId) {
      openModal.warning({
        title: (
          <div className="px-5 mb-5 w-80">
            <div>{t('components.share.confirm_close_share')}</div>
            <div>{t('components.share.close_share_desc')}</div>
          </div>
        ),
        confirmText: t('components.share.close_share_button'),
        confirm: () => {
          bizTracker.event('share_switch', { is_open: false });

          // 非顶层(即有 parent)时添加受限
          if (parentId) {
            permissions.forEach((o) => setPermission(uuid, o));
            setBlockPermission(uuid, { type: PermissionType.RESTRICTED });
          }
          removeBlockPermission(uuid, { type: PermissionType.PUBLIC });
          dispatch(TRANSACTION_FIRE());
        },
      });
    } else {
      bizTracker.event('share_switch', { is_open: !shared });

      if (shared) {
        removeBlockPermission(uuid, { type: PermissionType.PUBLIC });
      } else {
        openShareOrSwitchViewType.current = true;
        setBlockPermission(uuid, {
          role: PermissionRole.READER,
          type: PermissionType.PUBLIC,
        });

        // 积分任务
        if (isPageLike(block.type)) {
          void updateTask(ActivityIds.GUIDE_FIRST_TIME_SHARE, ActivitiesListType.advancedList);
          void updateTask(ActivityIds.OPEN_SHARE, ActivitiesListType.advancedList);
        }
      }

      dispatch(TRANSACTION_FIRE());
    }
  };

  const onClickAllDuplicate = () => {
    transaction(() => {
      void set().setRole('allowDuplicate');
    });
  };

  const onClickAllowSelectionCopy = () => {
    transaction(() => {
      void set().setRole('allowSelectionCopy');
    });
  };

  const onClickAllowDownload = () => {
    transaction(() => {
      void set().setRole('allowDownload');
    });
  };

  const onClickOpenToc = () => {
    transaction(() => {
      void set().setShareShowSidebar();
    });
  };

  const onClickOpenBreadcrumb = () => {
    transaction(() => {
      void set().setRole('allowShowBreadcrumb');
    });
  };

  const onClickAIChat = () => {
    transaction(() => {
      void set().setRole('allowShowAIChat');
    });
  };
  const onClickAllowSubscribe = () => {
    transaction(() => {
      void set().setAllowSubscribe();
    });
  };

  const onClickOpenSeo = () => {
    onChangeTab(Panel.SEO);
    // transaction(() => {
    //   void set().setShareSeo();
    // });
  };
  const onClickPasswordSetting = () => {
    bizTracker.event('share_pw');
    onChangeTab(Panel.PASSWORD);
  };
  const onClickLinkValid = (e: React.MouseEvent) => {
    if (!isEditor) return;
    if (isFreeSpaceLimit) {
      openAskUpgradeSpace(() => {
        openSettingModal({
          defaultMenuType: SettingMenuType.upgrade,
          from: OpenSettingFrom.link_valid,
        });
      });
      return;
    }

    if (roleStatus.isRestricted) {
      message.error(t('common.inherit_parent_setting'));
    } else {
      openModal.dropdown({
        placement: 'bottom-end',
        popcorn: e.currentTarget,
        content: (_) => {
          return (
            <ListView
              className="w-[200px] next-modal py-2"
              items={[
                {
                  type: ListItemType.OPERATION,
                  data: {
                    title: t('common.permanent_valid'),
                    onClick: () => {
                      _.onCloseModal();
                      transaction(() => {
                        void set().setLinkValidTime(undefined);
                      });
                    },
                    selected: !linkValidDate,
                  },
                },
                {
                  type: ListItemType.LINE,
                  data: {},
                  disableClick: true,
                },
                {
                  type: ListItemType.OPERATION,
                  data: {
                    title: (
                      <div>
                        <div>{t('components.share.custom_expiry_date')}</div>
                        {linkValidDate && (
                          <div className="mt-1 text-t4 text-grey3">{`${dayjs(linkValidDate).format(
                            dateFormat
                          )}`}</div>
                        )}
                      </div>
                    ),
                    onClick: (e: React.MouseEvent) => {
                      openModal.dropdown({
                        popcorn: elementToGetBoundingClientRect(e.currentTarget),
                        placement: 'top',
                        content: ({ onCloseModal }) => {
                          const date = dayjs(linkValidDate);
                          const today = new Date();
                          today.setDate(today.getDate());
                          return (
                            <DateTimePicker
                              uiOption={{
                                hasSwitchTimeOption: false,
                                hasFormatSetting: false,
                                disabledDays: {
                                  before: today,
                                },
                                footer: (
                                  <div className="px-4 text-t4 text-grey3 text-center">
                                    {t('components.share.expiry_time_tip')}
                                  </div>
                                ),
                              }}
                              closeDatePicker={onCloseModal}
                              dateInfo={{
                                from: date.isValid() ? date.toDate() : new Date(),
                                includeTime: false,
                              }}
                              onChange={({ from }) => {
                                _.onCloseModal();
                                from.setHours(23);
                                from.setMinutes(59);
                                transaction(() => {
                                  void set().setLinkValidTime(from.getTime());
                                });
                              }}
                            />
                          );
                        },
                      });
                    },
                    selected: linkValidDate,
                  },
                },
              ]}
            />
          );
        },
      });
    }
  };

  const onClickOpenDomainSetting = () => {
    openSettingModal({
      from: OpenSettingFrom.homepage,
      defaultMenuType: SettingMenuType.space,
    });
  };

  // 权限继承，受限逻辑跟分享开关一样
  const disableSettingShareViewOrComment = !isEditor || (!isRestricted && !!parentId);
  // 分享是否可评论
  const openShareLinkOption = (e: React.MouseEvent) => {
    if (!isEditor) return;
    if (!isRestricted && parentId) {
      message.error(t('common.inherit_parent_setting'));
      return;
    }
    openModal.dropdown({
      offset: [0, 5],
      popcorn: e.currentTarget as Element,
      placement: 'bottom',
      content: ({ onCloseModal }) => {
        return (
          <ListView
            className="next-modal py-1 w-72"
            items={[
              {
                type: ListItemType.TEXT_DESCRIPTION,
                data: {
                  title: (
                    <>
                      {t('components.share.any_person_link_access')}
                      <span className={cx('ml-1 text-t2-medium')}>
                        <span>{t('common.can_view')}</span>
                      </span>
                    </>
                  ),
                  selected: publicRole !== PermissionRole.COMMENTER,
                  onClick: () => {
                    if (publicRole === PermissionRole.COMMENTER) {
                      openShareOrSwitchViewType.current = true;
                      transaction(() => {
                        void set().setCommon(PermissionRole.READER);
                      });
                    }
                    onCloseModal();
                  },
                },
              },
              {
                type: ListItemType.TEXT_DESCRIPTION,
                data: {
                  title: (
                    <>
                      {t('components.share.any_person_link_access')}
                      <span className="ml-1 text-t2-medium">{t('common.can_comment')}</span>
                    </>
                  ),
                  selected: publicRole === PermissionRole.COMMENTER,
                  onClick: () => {
                    if (publicRole !== PermissionRole.COMMENTER) {
                      openShareOrSwitchViewType.current = true;
                      transaction(() => {
                        void set().setCommon(PermissionRole.COMMENTER);
                      });
                    }
                    onCloseModal();
                  },
                },
              },
            ]}
          />
        );
      },
    });
  };

  return (
    <div className="text-t2 overflow-auto max-h-[80vh]">
      <Tooltip
        delay={5000}
        className="w-full"
        disabled={!(isEditor && disableSwitchPublicShare)}
        popup={t('components.share.share_disabled_tip')}
      >
        <div
          className={cx(
            'w-full flex items-center justify-between px-4 py-3 animate-click animate-hover rounded-none',
            (!isEditor || disableSwitchPublicShare) && 'opacity-60 cursor-not-allowed'
          )}
          onClick={onClickSwitch}
        >
          <div className="flex items-center space-x-2">
            <span>{t('components.share_setting.public_share')}</span>
            {!isBuildIn() && isFreeSpaceLimit && <ProIcon absolute={false} />}
          </div>
          {!judgeSharePage() && (
            <div data-onboarding="public-switch" className="flex items-center">
              <Switch
                disabled={disableSwitchPublicShare}
                bgClassName="pointer-events-none"
                open={illegal ? false : shared}
              />
            </div>
          )}
        </div>
      </Tooltip>
      {shared && (
        <>
          <div className="flex items-center h-8 bg-grey9 rounded border-[1px] border-grey6 mx-4 my-1">
            <div
              className="relative ml-3 w-full animate-click text-ellipsis pr-8"
              onClick={openShareLinkOption}
            >
              {t('components.share.any_person_link_access')}
              <span
                className={cx('ml-1 text-t2-medium', {
                  'opacity-20': disableSettingShareViewOrComment,
                  'cursor-not-allowed': disableSettingShareViewOrComment,
                })}
              >
                {publicRole === PermissionRole.COMMENTER
                  ? t('common.can_comment')
                  : t('common.can_view')}
              </span>
              <Icon
                name={'IcArrowDown01'}
                size="xxxsmall"
                className="absolute right-2 top-[5px] text-grey3"
              />
            </div>
            <div className="w-[1.5px] h-full bg-grey6" />
            <div
              onClick={onCopyLink}
              id={copyShareId}
              data-clipboard-text={copyLink}
              className="flex h-full  flex-shrink-0 items-center px-3 font-medium bg-white2 animate-click"
            >
              <div className="text-t2-medium">
                {password
                  ? t('components.share.copy_link_password')
                  : t('common.components.link_widget.copy_link')}
              </div>
            </div>
          </div>
          <div hidden={__PRIVATE__} className="py-2 mx-4 text-t3 text-grey3 space-x-1">
            <span>{t('components.share.custom_domain')}</span>
            <span className="animate-click text-active_color" onClick={onClickOpenDomainSetting}>
              {currentSpace.domain ? currentSpace.domain : t('components.share.set_domain')}
            </span>
          </div>
          {!illegal && <PaySetting onChangeTab={onChangeTab} uuid={uuid} />}
          <div
            onClick={onClickPasswordSetting}
            className="animate-hover rounded-none animate-click w-full flex justify-between items-center px-4 py-3"
          >
            <div className="relative">
              <div>
                {password
                  ? t('components.share.password_enabled')
                  : t('components.share.password_disabled')}
              </div>
            </div>
            <button className="text-grey3 flex items-center">
              {password ? t('common.change_password') : t('common.set_password')}
            </button>
          </div>
          <div
            onClick={onClickLinkValid}
            className={cx(
              'w-full flex justify-between items-center px-4 py-3',
              !password && !readonly && isEditor && 'animate-hover rounded-none animate-click'
            )}
          >
            <div className="flex items-center space-x-1">
              <div>{t('components.share.link_validity')}</div>
              {isFreeSpaceLimit && <ProIcon absolute={false} />}
            </div>
            <button className="text-grey3 flex items-center">
              {linkValidDate
                ? `${dayjs(linkValidDate).format(dateFormat)}${t('components.share.expiry')}`
                : t('common.permanent_valid')}
              <Icon name="IcArrowDown01" size="xxxsmall" className="ml-1" />
            </button>
          </div>
          <div
            hidden={__PRIVATE__}
            onClick={onClickAllowSubscribe}
            className={cx(
              'flex justify-between items-center px-4 py-3',
              !readonly && isEditor && 'animate-hover rounded-none animate-click',
              ((accessFee && accessFee > 0) || roleStatus.isRestricted) && 'opacity-60'
            )}
          >
            <div>{t('components.share.allow_add_to_library')}</div>
            <Switch bgClassName="pointer-events-none" open={allowSubscribe} />
          </div>
          <div
            onClick={onClickAllDuplicate}
            className={cx(
              'flex justify-between items-center px-4 py-3',
              !readonly && isEditor && 'animate-hover rounded-none animate-click',
              (!isEditor || roleStatus.isRestricted) && 'opacity-60'
            )}
          >
            <div>{t('components.share.allow_copy_duplicate')}</div>
            <Switch bgClassName="pointer-events-none" open={allowDuplicate} />
          </div>
          <div
            onClick={onClickAllowSelectionCopy}
            className={cx(
              'flex justify-between items-center px-4 py-3',
              !readonly && isEditor && 'animate-hover rounded-none animate-click',
              (!isEditor || roleStatus.isRestricted) && 'opacity-60'
            )}
          >
            <div>{t('components.share.allow_copy_content')}</div>
            <Switch bgClassName="pointer-events-none" open={allowSelectionCopy} />
          </div>
          <div
            onClick={onClickAllowDownload}
            className={cx(
              'flex justify-between items-center px-4 py-3',
              !readonly && isEditor && 'animate-hover rounded-none animate-click',
              (!isEditor || roleStatus.isRestricted) && 'opacity-60'
            )}
          >
            <div>{t('components.share.allow_download')}</div>
            <Switch bgClassName="pointer-events-none" open={allowDownload} />
          </div>

          <div
            onClick={onClickOpenToc}
            className={cx(
              'flex justify-between items-center px-4 py-3',
              !readonly && isEditor && 'animate-hover rounded-none animate-click',
              (!isEditor || roleStatus.isRestricted) && 'opacity-60'
            )}
          >
            <div>{t('components.share.show_sidebar_toc')}</div>
            <Switch bgClassName="pointer-events-none" open={allowShowSidebar} />
          </div>
          <div
            onClick={onClickOpenBreadcrumb}
            className={cx(
              'flex justify-between items-center px-4 py-3',
              !readonly && isEditor && 'animate-hover rounded-none animate-click',
              (!isEditor || roleStatus.isRestricted) && 'opacity-60'
            )}
          >
            <div>{t('components.share.show_breadcrumb')}</div>
            <Switch bgClassName="pointer-events-none" open={allowShowBreadcrumb} />
          </div>
          <div
            hidden={!isPDFFile || !__PRIVATE__}
            onClick={onClickAIChat}
            className={cx(
              'flex justify-between items-center px-4 py-3',
              !readonly && isEditor && 'animate-hover rounded-none animate-click',
              (!isEditor || roleStatus.isRestricted) && 'opacity-60'
            )}
          >
            <div>{t('components.share.show_ai_chat')}</div>
            <Switch bgClassName="pointer-events-none" open={allowShowAIChat} />
          </div>

          {!__PRIVATE__ && (
            <Tooltip
              placement={password ? undefined : 'right'}
              className="w-full"
              popup={
                password
                  ? t('components.share.seo_disabled_with_password')
                  : createMenuDataContext({
                      title: t('components.share.seo_enabled_tip'),
                      image: __BUILD_IN__ ? 'google_search' : 'baidu_search',
                    })
              }
            >
              <div
                onClick={onClickOpenSeo}
                className={cx(
                  'w-full flex justify-between items-center px-4 py-3',
                  !password && !readonly && isEditor && 'animate-hover rounded-none animate-click',
                  (!!password || !isEditor || roleStatus.isRestricted) && 'opacity-60'
                )}
              >
                <div className="relative">
                  <div>{t('common.search_engine_promotion')}</div>
                </div>
                {allowSeo ? t('components.share.enabled') : t('common.not_enabled')}
              </div>
            </Tooltip>
          )}
          <ShareButtons
            uuid={uuid}
            url={url}
            onCopyLink={onCopyLink}
            onCopyEmbedLink={copyEmbedLink}
          />
        </>
      )}
      <ShareMainFooter uuid={uuid} />
    </div>
  );
};

const PaySetting: FC<Props> = (props) => {
  const { t } = useAppTranslation();
  const { uuid, onChangeTab } = props;
  let isFreeSpaceLimit = useIsFreeSpaceLimit();
  if (__BUILD_IN__) {
    // buildin无限制
    isFreeSpaceLimit = false;
  }
  const openAskUpgradeSpace = useOpenAskUpgradeSpace();
  const openSettingModal = useOpenSettingModal();
  const { accessFee = 0, accessFeeValidDays } = usePermissions(uuid);
  if (__PRIVATE__) return null;
  if (!FeatureFlags.OPEN_SUBSCRIBE) return null;

  return (
    <div
      className="px-4 flex items-center justify-between text-t2 py-3 animate-hover animate-click rounded-none"
      onClick={(e) => {
        e.stopPropagation();
        if (isFreeSpaceLimit) {
          openAskUpgradeSpace(() => {
            openSettingModal({
              defaultMenuType: SettingMenuType.upgrade,
              from: OpenSettingFrom.package,
            });
          });
          return;
        }
        bizTracker.event('share_pay');
        onChangeTab(Panel.PAY);
      }}
    >
      <div className="flex items-center space-x-2">
        <span>{t('common.paid_subscribe')}</span>
        {isFreeSpaceLimit && <ProIcon absolute={false} />}
      </div>
      <div
        data-onboarding="paid-subscription"
        className={cx('flex items-center', accessFee ? 'text-active_color' : 'text-grey3')}
      >
        {isFreeSpaceLimit && accessFee ? (
          <span>{t('components.share.expired')}</span>
        ) : (
          <span>
            {accessFee
              ? `${getCurrencySymbols()}${accessFee},${computePeriod(accessFeeValidDays)}`
              : t('common.not_enabled')}
          </span>
        )}
      </div>
    </div>
  );
};
